import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.addVirtualFileSystem(pdfFonts);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});
const amount = (value) => currency.format(Number(value || 0));
const date = (value) => (value ? new Date(value).toLocaleDateString("en-GB") : "N/A");
const auth = () => ({ Authorization: `Bearer ${sessionStorage.getItem("token")}` });
const userContext = () => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  return {
    clientId: Number(user.client_id || user.clientId),
    collegeId: Number(user.college_id || user.collegeId),
  };
};
const formatDetailValue = (key, value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  // Format date fields in Indian format: DD-MM-YYYY
  if (
    /date|_at$|_on$/i.test(key) ||
    (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value))
  ) {
    const date = new Date(value);

    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
    }
  }

  return String(value);
};
const logoUrl = (report, clientId) => {
  const path = report?.college?.logo_path;
  if (!path) return null;
  if (/^(data:|https?:)/i.test(path)) return path;
  if (path.startsWith("/uploads/")) return `${API_BASE_URL}${path}`;
  return `${API_BASE_URL}/uploads/logos/${clientId}/${report.college.society_id}/${report.college.college_id}/${path}`;
};

const imageDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
};

const sections = [
  {
    key: "allocations",
    title: "Fee Books / Fee Allocation Ledger",
    columns: [
      ["academic_year", "Academic Year"],
      ["year_of_study", "Year of Study"],
      ["semester", "Semester"],
      ["fee_head_name", "Fee Head"],
      ["fee_type", "Fee Type"],
      ["gross_fee", "Gross Fee", true],
      ["concession_amount", "Concession", true],
      ["net_fee_payable", "Net Payable", true],
    ],
  },
  {
    key: "concessions",
    title: "Concession Ledger",
    columns: [
      ["fee_head_name", "Fee Head"],
      ["concession_type", "Type"],
      ["academic_year", "Academic Year"],
      ["year_of_study", "Year of Study"],
      ["concession_amount", "Amount", true],
      ["approval_status", "Status"],
    ],
  },
  {
    key: "receipts",
    title: "Fee Receipt / Payment Ledger",
    columns: [
      ["receipt_number", "Receipt No."],
      ["receipt_date", "Receipt Date", false, true],
      ["academic_year", "Academic Year"],
      ["year_of_study", "Year of Study"],
      ["fee_head_name", "Fee Head"],
      ["amount_received", "Amount Received", true],
      ["payment_mode", "Mode"],
      ["transaction_reference", "Transaction / UTR"],
      ["receipt_status", "Status"],
    ],
  },
  {
    key: "refunds",
    title: "Fee Refund Ledger",
    columns: [
      ["refund_reference", "Refund Ref."],
      ["refund_date", "Refund Date", false, true],
      ["academic_year", "Academic Year"],
      ["year_of_study", "Year of Study"],
      ["fee_head_name", "Fee Head"],
      ["refund_amount", "Refund Amount", true],
      ["refund_mode", "Mode"],
      ["refund_status", "Status"],
    ],
  },
  {
    key: "fee_head_summary",
    title: "Fee-head-wise Summary ",
    columns: [
      ["fee_head_name", "Fee Head"],
      ["gross_fee", "Gross Fee", true],
      ["concession_amount", "Concession", true],
      ["net_fee_payable", "Net Payable", true],
      ["amount_received", "Received", true],
      ["refund_amount", "Refund", true],
      ["net_collected", "Net Collected", true],
      ["outstanding", "Fee Due", true],
      ["advance", "Advance", true],
    ],
  },
  {
    key: "academic_year_summary",
    title: "Academic Year / Year-of-study Summary ",
    columns: [
      ["academic_year", "Academic Year"],
      ["year_of_study", "Year of Study"],
      ["gross_fee", "Gross Fee", true],
      ["concession_amount", "Concession", true],
      ["net_fee_payable", "Net Payable", true],
      ["amount_received", "Received", true],
      ["refund_amount", "Refund", true],
      ["outstanding", "Fee Due", true],
      ["advance", "Advance", true],
    ],
  },
];

const gridSx = {
  border: "1px solid #d9e1ec",
  borderRadius: 1,
  backgroundColor: "#fff",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#e9edf3",
    color: "#344767",
    borderBottom: "1px solid #d9e1ec",
    minHeight: "52px !important",
    maxHeight: "52px !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#344767",
    fontWeight: 700,
    whiteSpace: "normal",
    lineHeight: 1.2,
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #eef2f7",
    alignItems: "center",
    whiteSpace: "normal",
    lineHeight: 1.35,
    py: 1,
  },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#f8fbff" },
  "& .MuiDataGrid-footerContainer": { minHeight: 44 },
};

const columnSizing = {
  academic_year: { width: 125 },
  year_of_study: { width: 125 },
  semester: { width: 125 },
  fee_head_name: { minWidth: 230, flex: 1.5 },
  fee_type: { minWidth: 140, flex: 0.8 },
  concession_type: { minWidth: 150, flex: 0.8 },
  approval_status: { width: 125 },
  receipt_number: { minWidth: 150, flex: 0.8 },
  receipt_date: { width: 125 },
  amount_received: { width: 145 },
  payment_mode: { width: 120 },
  transaction_reference: { minWidth: 180, flex: 1 },
  receipt_status: { width: 115 },
  refund_reference: { minWidth: 150, flex: 0.8 },
  refund_date: { width: 125 },
  refund_amount: { width: 145 },
  refund_mode: { width: 120 },
  refund_status: { width: 125 },
  gross_fee: { width: 135 },
  concession_amount: { width: 135 },
  net_fee_payable: { width: 145 },
  net_collected: { width: 145 },
  outstanding: { width: 140 },
  advance: { width: 130 },
};

const reportActionButtonSx = {
  minWidth: "max-content",
  height: 34,
  px: 1.75,
  borderRadius: "4px",
  fontSize: "0.65rem",
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: "nowrap",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.16)",
};

function downloadCsv(filename, rows) {
  const sheet = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(sheet);
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function safeSheetName(value, usedNames) {
  const withoutInvalidCharacters = [":", "\\", "/", "?", "*", "[", "]"].reduce(
    (name, character) => name.split(character).join("-"),
    String(value || "Sheet")
  );
  const cleaned = withoutInvalidCharacters
    .replace(/\s+/g, " ")
    .replace(/^'+|'+$/g, "")
    .trim();
  const baseName = (cleaned || "Sheet").slice(0, 31);
  let sheetName = baseName;
  let suffix = 2;
  while (usedNames.has(sheetName.toLowerCase())) {
    const suffixText = ` ${suffix}`;
    sheetName = `${baseName.slice(0, 31 - suffixText.length)}${suffixText}`;
    suffix += 1;
  }
  usedNames.add(sheetName.toLowerCase());
  return sheetName;
}

function escapeHtml(value) {
  return String(value ?? "N/A")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function StudentFeeHistory({ initialStudent = null }) {
  const { clientId, collegeId } = useMemo(userContext, []);
  const [query, setQuery] = useState(initialStudent?.student_rollno || "");
  const [students, setStudents] = useState(initialStudent ? [initialStudent] : []);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const search = async () => {
    if (!query || !query.trim()) {
      setError("Please enter a student name or roll number to search.");
      setStudents([]);
      setReport(null);
      return;
    }

    setLoading(true);
    setError("");
    setReport(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/student-fee-history/${clientId}/${collegeId}/students`,
        {
          params: { q: query.trim() },
          headers: auth(),
        }
      );

      setStudents(response.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to search students");
    } finally {
      setLoading(false);
    }
  };

  const view = async (student) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/student-fee-history/${clientId}/${collegeId}/students/${student.student_id}`,
        { params: { rollNo: student.student_rollno }, headers: auth() }
      );
      setReport(response.data.data);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load fee history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialStudent?.student_id && initialStudent?.student_rollno) view(initialStudent);
    // The Student module supplies the signed-in student's immutable identity once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detailedRows = () =>
    sections.flatMap((section) =>
      (report?.[section.key] || []).map((row) => ({ Section: section.title, ...row }))
    );
  const summaryRows = () =>
    Object.entries(report?.summary || {}).map(([key, value]) => ({
      Metric: key.replaceAll("_", " ").toUpperCase(),
      Amount: Number(value),
    }));

  const studentRows = students.map((student, index) => ({
    id: String(student.student_id),
    sno: index + 1,
    ...student,
  }));

  const studentColumns = [
    { field: "sno", headerName: "Sl. No.", width: 85, sortable: false },
    { field: "student_rollno", headerName: "Roll Number", minWidth: 160, flex: 0.8 },
    { field: "student_name", headerName: "Student Name", minWidth: 220, flex: 1.2 },
    { field: "program", headerName: "Program", minWidth: 160, flex: 0.8 },
    { field: "branch", headerName: "Branch", minWidth: 170, flex: 0.8 },
    { field: "admitted_batch", headerName: "Admitted Batch", width: 145 },
    {
      field: "action",
      headerName: "Action",
      width: 175,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <MDButton color="info" size="small" onClick={() => view(params.row)}>
          View Fee History
        </MDButton>
      ),
    },
  ];

  const reportRows = (section) =>
    (report?.[section.key] || []).map((row, index) => ({
      id: `${section.key}-${
        row.fee_book_id ||
        row.fee_concession_id ||
        row.fee_receipt_id ||
        row.fee_refund_id ||
        row.key ||
        index
      }`,
      sno: index + 1,
      ...row,
    }));

  const reportColumns = (section) => {
    const columns = [
      { field: "sno", headerName: "Sl. No.", width: 80, sortable: false },
      ...section.columns.map(([field, headerName, isAmount, isDate]) => ({
        field,
        headerName,
        ...(columnSizing[field] || { minWidth: 130, flex: 0.7 }),
        align: isAmount ? "right" : "left",
        headerAlign: isAmount ? "right" : "left",
        renderCell: (params) =>
          isAmount ? amount(params.value) : isDate ? date(params.value) : params.value ?? "N/A",
      })),
    ];
    if (section.key === "receipts" || section.key === "refunds") {
      columns.push({
        field: "action",
        headerName: "Action",
        width: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <MDButton
            size="small"
            color="info"
            onClick={() =>
              setDetail({
                title: section.key === "receipts" ? "Receipt Details" : "Refund Details",
                row: params.row,
              })
            }
          >
            {section.key === "receipts" ? "View Receipt Details" : "View Refund Details"}
          </MDButton>
        ),
      });
    }
    return columns;
  };

  const exportOds = () => {
    const workbook = XLSX.utils.book_new();
    const usedSheetNames = new Set();
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet([{ ...report.student, generated_at: report.generated_at }]),
      safeSheetName("Student", usedSheetNames)
    );
    sections.forEach((section) =>
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(report[section.key] || []),
        safeSheetName(section.title, usedSheetNames)
      )
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(summaryRows()),
      safeSheetName("Summary", usedSheetNames)
    );
    XLSX.writeFile(workbook, `Student_Fee_History_${report.student.roll_number}.ods`, {
      bookType: "ods",
    });
  };

  const printReport = () => {
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      setError("Print window was blocked. Please allow pop-ups and try again.");
      return;
    }

    const institutionDetails = [
      report.college.address,
      report.college.email,
      report.college.website,
      report.college.affiliation,
      report.college.accreditation,
    ]
      .filter(Boolean)
      .map(escapeHtml)
      .join(" | ");
    const studentDetails = [
      ["Roll Number", report.student.roll_number],
      ["Student Name", report.student.name],
      ["Student ID", report.student.student_id],
      ["Program", report.student.program],
      ["Branch", report.student.branch],
      ["Admitted Batch", report.student.admitted_batch],
      ["Admission Year", report.student.admission_year],
      ["Current Year", report.student.current_year],
      ["Current Semester", report.student.current_semester],
      ["Academic Status", report.student.academic_status],
    ];
    const summaryDetails = [
      ["Gross Fee", report.summary.total_gross_fee],
      ["Concession", report.summary.total_concession],
      ["Net Fee Payable", report.summary.net_fee_payable],
      ["Total Received", report.summary.total_received],
      ["Total Refunds", report.summary.total_refund],
      ["Net Collected", report.summary.net_collected],
      ["Outstanding", report.summary.outstanding],
      ["Advance", report.summary.advance],
    ];
    const reportTables = sections
      .map((section) => {
        const rows = report[section.key] || [];
        const body = rows.length
          ? rows
              .map(
                (row, index) =>
                  `<tr><td class="number">${index + 1}</td>${section.columns
                    .map(([field, , isAmount, isDate]) => {
                      const value = isAmount
                        ? amount(row[field])
                        : isDate
                        ? date(row[field])
                        : row[field] ?? "N/A";
                      return `<td class="${isAmount ? "money" : ""}">${escapeHtml(value)}</td>`;
                    })
                    .join("")}</tr>`
              )
              .join("")
          : `<tr><td colspan="${section.columns.length + 1}" class="empty">No records</td></tr>`;
        return `<section><h2>${escapeHtml(
          section.title
        )}</h2><table><thead><tr><th class="number">Sl. No.</th>${section.columns
          .map(
            ([, headerName, isAmount]) =>
              `<th class="${isAmount ? "money" : ""}">${escapeHtml(headerName)}</th>`
          )
          .join("")}</tr></thead><tbody>${body}</tbody></table></section>`;
      })
      .join("");
    const logo = logoUrl(report, clientId);

    printWindow.document.open();
    printWindow.document
      .write(`<!doctype html><html><head><meta charset="utf-8"><title>Student Fee History - ${escapeHtml(
      report.student.roll_number
    )}</title><style>
      @page { size: A4 landscape; margin: 10mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #263852; font-family: Arial, sans-serif; font-size: 9px; }
      .header { text-align: center; border-bottom: 2px solid #315b85; padding-bottom: 7px; margin-bottom: 8px; }
      .logo { max-width: 58px; max-height: 58px; margin-bottom: 3px; }
      h1 { margin: 2px 0; color: #344767; font-size: 18px; }
      .institution { margin: 2px auto; max-width: 95%; color: #52657d; font-size: 9px; }
      .title { margin: 7px 0 3px; font-size: 15px; font-weight: 700; }
      .reference { font-size: 8px; color: #607086; }
      .details { display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px; margin: 7px 0; }
      .detail { border: 1px solid #d8e0ea; padding: 5px; min-height: 36px; }
      .label { display: block; color: #6b778c; font-size: 7px; margin-bottom: 2px; }
      .value { font-weight: 700; font-size: 9px; overflow-wrap: anywhere; }
      .summary { display: grid; grid-template-columns: repeat(8, 1fr); gap: 4px; margin: 7px 0 9px; }
      .summary-item { background: #edf5ff; border: 1px solid #b9cde5; padding: 5px; }
      section { margin: 8px 0 10px; }
      h2 { margin: 0 0 4px; color: #315b85; font-size: 11px; break-after: avoid; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      th { background: #e9edf3; color: #344767; font-weight: 700; }
      th, td { border: 1px solid #cdd6e1; padding: 4px; vertical-align: middle; overflow-wrap: anywhere; }
      .number { width: 35px; text-align: center; }
      .money { text-align: right; white-space: nowrap; }
      .empty { text-align: center; padding: 8px; color: #6b778c; }
    </style></head><body>
     <header class="header">
  ${
    logo
      ? `<div class="logo-container">
           <img class="logo" src="${escapeHtml(logo)}" alt="College logo">
         </div>`
      : ""
  }
  <div class="college-details">
    <h1>${escapeHtml(report.college.name)}</h1>
    <div class="institution">${institutionDetails}</div>
  </div>
 

        <div class="title">STUDENT FEE HISTORY REPORT</div>
        <div class="reference">Reference: ${escapeHtml(
          report.report_reference
        )} | Generated: ${escapeHtml(new Date(report.generated_at).toLocaleString())}</div>
      </header>
      <div class="details">${studentDetails
        .map(
          ([label, value]) =>
            `<div class="detail"><span class="label">${escapeHtml(
              label
            )}</span><span class="value">${escapeHtml(value)}</span></div>`
        )
        .join("")}</div>
      <div class="summary">${summaryDetails
        .map(
          ([label, value]) =>
            `<div class="summary-item"><span class="label">${escapeHtml(
              label
            )}</span><span class="value">${escapeHtml(amount(value))}</span></div>`
        )
        .join("")}</div>
      ${reportTables}
    </body></html>`);
    printWindow.document.close();
    printWindow.addEventListener("load", () => {
      window.setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    });
  };

  const exportPdf = async () => {
    const logo = await imageDataUrl(logoUrl(report, clientId));
    const tableBlocks = sections.flatMap((section) => [
      { text: section.title, style: "section" },
      {
        table: {
          headerRows: 1,
          widths: section.columns.map(() => "auto"),
          body: [
            section.columns.map((c) => ({ text: c[1], bold: true, fillColor: "#dbeafe" })),
            ...(report[section.key] || []).map((row) =>
              section.columns.map((c) =>
                c[2] ? amount(row[c[0]]) : c[3] ? date(row[c[0]]) : String(row[c[0]] ?? "N/A")
              )
            ),
          ],
        },
        layout: "lightHorizontalLines",
        fontSize: 7,
        margin: [0, 0, 0, 10],
      },
    ]);
    pdfMake
      .createPdf({
        pageOrientation: "portrait",
        pageMargins: [24, 28, 24, 28],
        footer: (page, pages) => ({
          text: `Page ${page} of ${pages}  |  Generated ${new Date(
            report.generated_at
          ).toLocaleString()}`,
          alignment: "center",
          fontSize: 8,
        }),
        content: [
          ...(logo ? [{ image: logo, width: 60, alignment: "center", margin: [0, 0, 0, 4] }] : []),
          { text: report.college.name, alignment: "center", bold: true, fontSize: 17 },
          {
            text: [report.college.address, report.college.affiliation, report.college.accreditation]
              .filter(Boolean)
              .join("\n"),
            alignment: "center",
            fontSize: 9,
          },
          {
            text: "STUDENT FEE HISTORY REPORT",
            alignment: "center",
            bold: true,
            fontSize: 14,
            margin: [0, 8, 0, 10],
          },
          {
            text: `Report reference: ${report.report_reference}`,
            alignment: "center",
            fontSize: 8,
            margin: [0, 0, 0, 8],
          },
          {
            table: {
              widths: ["auto", "*", "auto", "*"],
              body: [
                ["Roll Number", report.student.roll_number, "Student Name", report.student.name],
                [
                  "Student ID",
                  report.student.student_id,
                  "Program / Branch",
                  `${report.student.program} / ${report.student.branch}`,
                ],
                [
                  "Admitted Batch",
                  report.student.admitted_batch,
                  "Academic Status",
                  report.student.academic_status,
                ],
              ],
            },
            layout: "lightHorizontalLines",
            margin: [0, 0, 0, 10],
          },
          ...tableBlocks,
          { text: "Final Financial Summary", style: "section" },
          {
            table: { body: summaryRows().map((row) => [row.Metric, amount(row.Amount)]) },
            layout: "lightHorizontalLines",
          },
        ],
        styles: { section: { bold: true, fontSize: 11, color: "#0f4c81", margin: [0, 8, 0, 4] } },
        defaultStyle: { fontSize: 8 },
      })
      .download(`Student_Fee_History_${report.student.roll_number}.pdf`);
  };

  return (
    <MDBox
      p={2}
      sx={{
        "@page": { size: "landscape", margin: "10mm" },
        "@media print": {
          ".no-print": { display: "none !important" },
          ".screen-grid": { display: "none !important" },
          ".print-only": { display: "block !important" },
          ".MuiPaper-root": { boxShadow: "none !important" },
          thead: { display: "table-header-group" },
          tfoot: { display: "table-footer-group" },
          table: { breakInside: "auto" },
          tr: { breakInside: "avoid" },
        },
      }}
    >
      <MDTypography variant="h5" mb={2}>
        STUDENT FEE HISTORY
      </MDTypography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!initialStudent && (
        <Box display="flex" gap={1} mb={2} className="no-print">
          <TextField
            size="medium"
            label="Roll Number or Name"
            value={query}
            required
            error={!query.trim() && Boolean(error)}
            helperText={!query.trim() && error ? error : ""}
            onChange={(e) => {
              setQuery(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                search();
              }
            }}
          />

          <MDButton color="info" onClick={search} disabled={!query.trim() || loading}>
            Search Student
          </MDButton>
        </Box>
      )}
      {loading && (
        <Box textAlign="center" p={3}>
          <CircularProgress />
        </Box>
      )}
      {!report && !loading && students.length > 0 && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <DataGrid
            autoHeight
            rows={studentRows}
            columns={studentColumns}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={gridSx}
          />
        </Box>
      )}
      {report && (
        <>
          <Grid container spacing={1} mb={2}>
            {Object.entries({
              "Roll Number": report.student.roll_number,
              "Student Name": report.student.name,
              "Student ID": report.student.student_id,
              Program: report.student.program,
              Branch: report.student.branch,
              "Admitted Batch": report.student.admitted_batch,
              "Admission Year": report.student.admission_year,
              "Current Year": report.student.current_year,
              "Current Semester": report.student.current_semester,
              "Academic Status": report.student.academic_status,
            }).map(([k, v]) => (
              <Grid item xs={12} sm={6} md={3} key={k}>
                <Paper sx={{ p: 1.5 }}>
                  <MDTypography variant="caption" color="text">
                    {k}
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold">
                    {v || "N/A"}
                  </MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={1} mb={2}>
            {[
              ["Gross Fee", "total_gross_fee"],
              ["Concession", "total_concession"],
              ["Net Fee Payable", "net_fee_payable"],
              ["Total Received", "total_received"],
              ["Total Refunds", "total_refund"],
              [
                report.summary.advance > 0 ? "Advance" : "Fee Due",
                report.summary.advance > 0 ? "advance" : "outstanding",
              ],
            ].map(([label, key]) => (
              <Grid item xs={6} md={2} key={key}>
                <Paper sx={{ p: 1.5, bgcolor: "#eef6ff" }}>
                  <MDTypography variant="caption">{label}</MDTypography>
                  <MDTypography variant="h6">{amount(report.summary[key])}</MDTypography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {sections.map((section) => (
            <Box key={section.key} mb={2}>
              <MDTypography variant="h6" mb={1}>
                {section.title}
              </MDTypography>
              <Box className="screen-grid" sx={{ width: "100%" }}>
                <DataGrid
                  autoHeight
                  rows={reportRows(section)}
                  columns={reportColumns(section)}
                  disableRowSelectionOnClick
                  getRowHeight={() => "auto"}
                  hideFooter={(report[section.key] || []).length <= 10}
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  localeText={{ noRowsLabel: "No records" }}
                  sx={gridSx}
                />
              </Box>
              <TableContainer
                className="print-only"
                component={Paper}
                sx={{ display: "none", border: "1px solid #b8c2cf", overflow: "visible" }}
              >
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    "& th": {
                      backgroundColor: "#e9edf3",
                      color: "#344767",
                      fontWeight: 700,
                      fontSize: "7.5pt",
                      lineHeight: 1.2,
                      border: "1px solid #b8c2cf",
                      p: 0.6,
                    },
                    "& td": {
                      fontSize: "7.5pt",
                      lineHeight: 1.25,
                      border: "1px solid #d9e1ec",
                      p: 0.6,
                      overflowWrap: "anywhere",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 38 }}>Sl. No.</TableCell>
                      {section.columns.map(([field, headerName, isAmount]) => (
                        <TableCell key={field} align={isAmount ? "right" : "left"}>
                          {headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report[section.key] || []).map((row, index) => (
                      <TableRow key={`print-${section.key}-${index}`}>
                        <TableCell>{index + 1}</TableCell>
                        {section.columns.map(([field, , isAmount, isDate]) => (
                          <TableCell key={field} align={isAmount ? "right" : "left"}>
                            {isAmount
                              ? amount(row[field])
                              : isDate
                              ? date(row[field])
                              : row[field] ?? "N/A"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {!report[section.key]?.length && (
                      <TableRow>
                        <TableCell colSpan={section.columns.length + 1} align="center">
                          No records
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
          <Paper
            className="no-print"
            elevation={2}
            sx={{
              mt: 1,
              px: 0.5,
              py: 1,
              borderRadius: 0,
              overflowX: "auto",
              borderTop: "1px solid #e5e7eb",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Box display="flex" gap={0.75} alignItems="center" sx={{ minWidth: "max-content" }}>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                sx={reportActionButtonSx}
                onClick={exportPdf}
              >
                DOWNLOAD PDF
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                sx={reportActionButtonSx}
                onClick={exportOds}
              >
                DOWNLOAD ODS / ODF
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                sx={reportActionButtonSx}
                onClick={() =>
                  downloadCsv(
                    `Student_Fee_History_Detailed_${report.student.roll_number}.csv`,
                    detailedRows()
                  )
                }
              >
                EXPORT DETAILED CSV
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                sx={reportActionButtonSx}
                onClick={() =>
                  downloadCsv(
                    `Student_Fee_History_Summary_${report.student.roll_number}.csv`,
                    summaryRows()
                  )
                }
              >
                EXPORT SUMMARY CSV
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                sx={reportActionButtonSx}
                onClick={printReport}
              >
                PRINT REPORT
              </MDButton>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                sx={{ ...reportActionButtonSx, boxShadow: "none", backgroundColor: "#fff" }}
                onClick={() => setReport(null)}
              >
                SEARCH STUDENT
              </MDButton>
            </Box>
          </Paper>
          <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
            <DialogTitle>{detail?.title}</DialogTitle>

            <DialogContent dividers>
              {Object.entries(detail?.row || {}).map(([key, value]) => (
                <Box
                  key={key}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={2}
                  py={0.75}
                >
                  <MDTypography
                    variant="caption"
                    sx={{ fontWeight: 600, flex: 1, color: "text.secondary" }}
                  >
                    {key.replaceAll("_", " ").toUpperCase()}
                  </MDTypography>

                  <MDTypography
                    variant="body2"
                    sx={{ flex: 1, textAlign: "right", overflowWrap: "anywhere" }}
                  >
                    {formatDetailValue(key, value)}
                  </MDTypography>
                </Box>
              ))}
            </DialogContent>

            <DialogActions>
              <MDButton color="info" onClick={() => setDetail(null)}>
                Close
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      )}
    </MDBox>
  );
}

StudentFeeHistory.propTypes = {
  initialStudent: PropTypes.shape({
    student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    student_rollno: PropTypes.string,
    student_name: PropTypes.string,
    program: PropTypes.string,
    branch: PropTypes.string,
    admitted_batch: PropTypes.string,
  }),
};

export default StudentFeeHistory;
